import type { ChatMessage, ObjectUpdateItem } from '../types'
import {
  collapseObjectUpdateMessagesByRunId,
  objectUpdateToItems,
} from './object-update-utils'

export type ThreadHistoryKind =
  | 'object_update'
  | 'file_added'
  | 'feedback'
  | 'human_note'

export type ThreadHistoryMessage = {
  id: string | number
  text: string
  created_at: string
  message_type: string
  run_id?: string | null
  object_update?: {
    object_type?: string
    object_id?: string
    object_name?: string
    action?: string
    source_tool?: string
    description?: string | null
    target_path?: string | null
    object_update_id?: string
    focus_key?: string
    changed_fields?: string[]
    display_label?: string
  } | null
  attachments?: Array<{
    id: string
    original_name: string
    mime_type?: string
    file_asset_id?: string
    created_at?: string
  }> | null
}

export type ThreadHistoryFile = {
  id: string
  original_name: string
  created_at?: string | null
}

export type ThreadHistoryFeedback = {
  score: number
  tags: string[]
  comment: string
  at: string | null
  message_id: string
  run_id: string
}

export type ThreadHistoryRow = {
  id: string
  kind: ThreadHistoryKind
  at: string
  primary: string
  secondary?: string
  badge: string
  target_path?: string
  focus_key?: string
}

export const THREAD_HISTORY_INITIAL_CAP = 30

const KIND_BADGE: Record<ThreadHistoryKind, string> = {
  object_update: 'Update',
  file_added: 'File',
  feedback: 'Feedback',
  human_note: 'Upload',
}

function toChatObjectUpdateMessage(msg: ThreadHistoryMessage): ChatMessage {
  const ou = msg.object_update
  return {
    id: String(msg.id),
    content: msg.text || '',
    isUser: false,
    runId: msg.run_id ? String(msg.run_id) : undefined,
    messageType: 'object_update',
    objectUpdate: ou
      ? {
          object_type: ou.object_type,
          object_id: ou.object_id,
          object_name: ou.object_name,
          action: ou.action,
          source_tool: ou.source_tool,
          description: ou.description ?? undefined,
          target_path: ou.target_path ?? undefined,
          object_update_id: ou.object_update_id,
          focus_key: ou.focus_key,
          changed_fields: ou.changed_fields,
          display_label: ou.display_label,
        }
      : undefined,
  }
}

function objectUpdatePrimary(
  item: ObjectUpdateItem,
  messageText: string
): string {
  const description = item.description?.trim()
  if (description) return description
  const text = messageText.trim()
  if (text) return text
  if (item.display_label?.trim()) return item.display_label.trim()
  const name = item.object_name?.trim() || item.object_type || 'Object'
  const action = item.action || 'updated'
  return `${name} ${action}`
}

function objectUpdateSecondary(item: ObjectUpdateItem): string | undefined {
  if (item.description?.trim()) {
    const label =
      item.display_label?.trim() ||
      `${item.object_type || 'Object'} ${item.action || 'updated'}`
    return label
  }
  if (item.changed_fields?.length) {
    return `Fields: ${item.changed_fields.join(', ')}`
  }
  if (item.source_tool) return `via ${item.source_tool}`
  return undefined
}

function formatUploadLabel(names: string[]): string {
  if (names.length === 0) return 'Uploaded file'
  if (names.length === 1) return `Uploaded ${names[0]}`
  if (names.length === 2) return `Uploaded ${names[0]} and ${names[1]}`
  return `Uploaded ${names[0]} and ${names.length - 1} more`
}

function formatFeedbackPrimary(fb: ThreadHistoryFeedback): string {
  const parts = [`Score ${fb.score}`]
  if (fb.tags?.length) parts.push(fb.tags.join(', '))
  return parts.join(' · ')
}

/**
 * Compose a reverse-chronological case-file History ledger from detail + files.
 */
export function composeThreadHistory(input: {
  messages?: ThreadHistoryMessage[] | null
  files?: ThreadHistoryFile[] | null
  feedbackItems?: ThreadHistoryFeedback[] | null
}): ThreadHistoryRow[] {
  const messages = input.messages ?? []
  const files = input.files ?? []
  const feedbackItems = input.feedbackItems ?? []
  const rows: ThreadHistoryRow[] = []
  const coveredAssetIds = new Set<string>()

  const objectUpdateMessages = messages.filter(
    (m) => m.message_type === 'object_update'
  )
  const collapsed = collapseObjectUpdateMessagesByRunId(
    objectUpdateMessages.map(toChatObjectUpdateMessage)
  )
  const createdAtById = new Map(
    objectUpdateMessages.map((m) => [String(m.id), m.created_at])
  )
  const textById = new Map(
    objectUpdateMessages.map((m) => [String(m.id), m.text || ''])
  )

  for (const msg of collapsed) {
    const at = createdAtById.get(msg.id) || ''
    if (!at) continue
    const items = objectUpdateToItems(msg.objectUpdate)
    const messageText = textById.get(msg.id) || msg.content || ''
    if (items.length === 0) {
      rows.push({
        id: `ou-${msg.id}`,
        kind: 'object_update',
        at,
        primary: messageText.trim() || 'Object updated',
        badge: KIND_BADGE.object_update,
      })
      continue
    }
    items.forEach((item, idx) => {
      rows.push({
        id: `ou-${msg.id}-${item.object_update_id || item.object_id || idx}`,
        kind: 'object_update',
        at,
        primary: objectUpdatePrimary(item, messageText),
        secondary: objectUpdateSecondary(item),
        badge: KIND_BADGE.object_update,
        target_path: item.target_path,
        focus_key: item.focus_key,
      })
    })
  }

  for (const msg of messages) {
    if (msg.message_type !== 'human') continue
    const atts = msg.attachments?.filter((a) => a.original_name) ?? []
    if (atts.length === 0) continue
    for (const att of atts) {
      if (att.file_asset_id) coveredAssetIds.add(att.file_asset_id)
    }
    const names = atts.map((a) => a.original_name)
    rows.push({
      id: `human-${msg.id}`,
      kind: 'human_note',
      at: msg.created_at,
      primary: formatUploadLabel(names),
      badge: KIND_BADGE.human_note,
    })
  }

  for (const file of files) {
    if (!file.created_at) continue
    if (coveredAssetIds.has(file.id)) continue
    rows.push({
      id: `file-${file.id}`,
      kind: 'file_added',
      at: file.created_at,
      primary: formatUploadLabel([file.original_name]),
      badge: KIND_BADGE.file_added,
    })
  }

  for (const fb of feedbackItems) {
    if (!fb.at) continue
    rows.push({
      id: `fb-${fb.message_id || fb.run_id || fb.at}`,
      kind: 'feedback',
      at: fb.at,
      primary: formatFeedbackPrimary(fb),
      secondary: fb.comment?.trim() || undefined,
      badge: KIND_BADGE.feedback,
    })
  }

  return rows.sort((a, b) => {
    const tb = Date.parse(b.at)
    const ta = Date.parse(a.at)
    if (Number.isNaN(tb) && Number.isNaN(ta)) return 0
    if (Number.isNaN(tb)) return -1
    if (Number.isNaN(ta)) return 1
    if (tb !== ta) return tb - ta
    return a.id.localeCompare(b.id)
  })
}
